import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const getMainCategory = createAsyncThunk(
  "category/getMainCategory",
  async (search = '', { rejectWithValue }) => {
    try {
      const response = await api.get("admin/get-categories?search="+search);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to add category" }
      );
    }
  }
);

export const getCategory = createAsyncThunk(
  "category/getCategory",
  async (parent_id = '', { rejectWithValue }) => {
    try {
      const response = await api.get("admin/get-sub-categories?parent_id="+parent_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to add category" }
      );
    }
  }
);

export const getSubCategory = createAsyncThunk(
  "category/getSubCategory",
  async (parent_id = '', { rejectWithValue }) => {
    try {
      const response = await api.get("admin/get-sub-categories?parent_id="+parent_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to add category" }
      );
    }
  }
);


export const addCategory = createAsyncThunk(
  "category/addCategory",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        let value = data[key];
        if (key === "image") {
          if (value instanceof File) { 
            formData.append(key, value);
          }
        }else {
          formData.append(key, value);
        }
      });
      let url = '';
      if (data?.id) {
        url = "/" + data.id;
      }
      const response = await api.post("admin/categories"+url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to add category" }
      );
    }
  }
);


export const deleteCategory = createAsyncThunk(
  "category/deleteCategory",
  async ({ id, type }, { rejectWithValue }) => {
    try {

      const response = await api.del(`admin/categories/${id}`);

      return { id, type, status: response.status };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to add category" }
      );
    }
  }
);


export const featuredCategory = createAsyncThunk(
  "category/featuredCategory",
  async ({ id, type }, { rejectWithValue }) => {
    try {

      const response = await api.post(`admin/category/feature`, {category_id:id, is_featured:type});

      return { id, type, status: response.status };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { status: "error", message: "Failed to load category" }
      );
    }
  }
);

export const downloadCategoriesCSV = createAsyncThunk(
  "category/downloadCategoriesCSV",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/export-categories`);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to export categories.");
    }
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState: {
    list_main_categories: [],
    list_categories: [],
    list_sub_categories: [],
    selected: null,
    status: "idle",
    loading: false,
    main_category_loading: false,
    category_loading: false,
    sub_category_loading:false
  },
  reducers: {
    setSelectedCategory(state, action) {
      const payload = action.payload || {};
      state.selected = {
        id: payload.id || "",
        name: payload.name || null,
        description: payload.description || "",
        image: payload.image,
        parent_id: payload.parent_id || '',
        status:payload.status
      };
    },
    unsetSelectedCategory(state) {
      state.selected = null;
    },
    resetStatus(state) {
      state.status  = "idle";
      state.error   = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getMainCategory.pending, (state) => {
        state.main_category_loading = true;
      })
      .addCase(getMainCategory.fulfilled, (state, action) => {
        state.main_category_loading = false;
        state.list_main_categories = action.payload;
      })
      .addCase(getMainCategory.rejected, (state, action) => {
        state.main_category_loading = false;
      })
      .addCase(getCategory.pending, (state) => {
        state.category_loading = true;
      })
      .addCase(getCategory.fulfilled, (state, action) => {
        state.category_loading = false;
        state.list_categories = action.payload;
      })
      .addCase(getCategory.rejected, (state, action) => {
        state.category_loading = false;
      })
      .addCase(getSubCategory.pending, (state) => {
        state.sub_category_loading = true;
      })
      .addCase(getSubCategory.fulfilled, (state, action) => {
        state.sub_category_loading = false;
        state.list_sub_categories = action.payload;
      })
      .addCase(getSubCategory.rejected, (state, action) => {
        state.sub_category_loading = false;
      })
      .addCase(featuredCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(featuredCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(featuredCategory.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(downloadCategoriesCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadCategoriesCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadCategoriesCSV.rejected, (state) => {
        state.loading = false;
      }); 
  },
});

export const { setSelectedCategory, unsetSelectedCategory, resetStatus } = categorySlice.actions;
export default categorySlice.reducer;
